import { useRef, useState } from 'react'
import type {
  MultiChoiceContent,
  SingleChoiceContent,
} from '@sciolingo/api-client'
import type { InteractionEvent } from '../types'
import { Markup } from '../Markup'

interface ChoiceItemProps {
  mode: 'single' | 'multi'
  content: SingleChoiceContent | MultiChoiceContent
  initialAnswer?: { optionId: string } | { optionIds: string[] }
  disabled: boolean
  onAnswerChange(answer: unknown): void
  onInteraction(event: InteractionEvent): void
}

/**
 * One component for both choice types, and one component for phone and desktop
 * (§9 rule 4) — responsiveness lives in the stylesheet, not in a second
 * component tree.
 */
export function ChoiceItem({
  mode,
  content,
  initialAnswer,
  disabled,
  onAnswerChange,
  onInteraction,
}: ChoiceItemProps) {
  const [selected, setSelected] = useState<string[]>(() => initialSelection(initialAnswer))

  // The current selection also lives in a ref, because two taps landing in the
  // same React batch would both read the state captured at the last render and
  // the second would discard the first. On a touch screen that is a real
  // sequence, not a synthetic one.
  const selectedRef = useRef(selected)

  function toggle(optionId: string): void {
    if (disabled) return

    const current = selectedRef.current
    const next =
      mode === 'single'
        ? [optionId]
        : current.includes(optionId)
          ? current.filter((id) => id !== optionId)
          : [...current, optionId]

    selectedRef.current = next
    setSelected(next)
    // An event handler, not render: the compiler cannot tell from here.
    // eslint-disable-next-line react-hooks/purity
    onInteraction({ kind: 'option-toggled', optionId, at: Date.now() })
    onAnswerChange(
      mode === 'single'
        ? { optionId: next[0] ?? '' }
        : { optionIds: [...next].sort() },
    )
  }

  return (
    <div className="sl-item sl-item--choice" data-mode={mode}>
      <p className="sl-item__prompt">
        <Markup text={content.prompt} />
      </p>
      {content.hint ? <p className="sl-item__hint">{content.hint}</p> : null}

      <ul className="sl-options" role={mode === 'single' ? 'radiogroup' : 'group'}>
        {content.options.map((option) => {
          const isSelected = selected.includes(option.id)
          return (
            <li key={option.id}>
              <button
                type="button"
                className="sl-option"
                role={mode === 'single' ? 'radio' : 'checkbox'}
                aria-checked={isSelected}
                data-selected={isSelected}
                disabled={disabled}
                onClick={() => toggle(option.id)}
                onFocus={() => onInteraction({ kind: 'focus', at: Date.now() })}
              >
                <span className="sl-option__marker" aria-hidden="true">
                  {option.id.toUpperCase()}
                </span>
                <span className="sl-option__text">
                  <Markup text={option.text} />
                </span>
              </button>
            </li>
          )
        })}
      </ul>
    </div>
  )
}

function initialSelection(answer: ChoiceItemProps['initialAnswer']): string[] {
  if (!answer) return []
  if ('optionId' in answer) return answer.optionId ? [answer.optionId] : []
  return answer.optionIds
}
